import { withTranslate } from "@pathwright/ui/src/components/"
import Button from "@pathwright/ui/src/components/button/Button"
import ExecuteScripts from "@pathwright/ui/src/components/embed/ExecuteScripts"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Success from "@pathwright/ui/src/components/success/Success"
import PathwrightUI from "@pathwright/ui/src/components/ui/PathwrightUI"
import Text from "@pathwright/ui/src/components/ui/Text"
import {
  ENROLLMENT_ROLE_FACILITATOR,
  ROLE_ACTIONS,
  ROLE_LABELS,
  ROLE_TYPE_OFFERING
} from "@pathwright/web/src/modules/invitation/constants"
import ShareIcons from "global/social/components/ShareIcons"
import ConnectStores from "lib/core/store/components/ConnectStores"
import * as urls from "lib/core/utils/urls"
import { injectVariable } from "lib/core/utils/variables"
import { usePathLoading } from "library/register/components/usePathLoading"
import { PathContext } from "library/show/components/path/state/PathProvider"
import { useContext, useEffect } from "react"
import "./Registered.css"

const tPrefix = "registration.registered"

const Registered = ({
  resource,
  resource_type,
  registration,
  shareInfo,
  trackingCode,
  invitation,
  invitationAction,
  registrationRoleName,
  alreadyRegistered,
  toPathURL,
  t,
  Trans
}) => {
  const { isWaitingForPath, pathFailingToLoad } = usePathLoading()
  const invitedToDifferentRole =
    !!invitation && invitation.role > registration.role

  const primaryAction = {
    to: toPathURL,
    submitting: isWaitingForPath,
    children: (
      <>
        {alreadyRegistered
          ? t(`registration.start_path.continue_learning`)
          : t(`registration.start_path.get_started`)}{" "}
        <Pathicon icon="chevron-right" />
      </>
    )
  }

  const meta = isWaitingForPath && (
    <>
      {t(`registration.start_path.preparing_path`)}...
      {pathFailingToLoad && (
        <div>
          <span>{t(`registration.start_path.taking_longer`)}</span>
          <br />
          {/* prettier-ignore */}
          {/* Trans formatting integral to translation json! */}
          <Trans
            parent="span"
            i18nKey={`registration.start_path.contact_support`}
          >
            Please{" "}
            <Button
              to={urls.modalRoute("/help/")}
              styleType="blank"
              brand={true}
              style={{ padding: 0 }}
              inverted
            >
              {{ contactSupport: "contact support" }}
            </Button>{" "}
            if the delay continues.
          </Trans>
        </div>
      )}
    </>
  )

  const roleDiff = invitedToDifferentRole && (
    <>
      {/* prettier-ignore */}
      {/* Trans formatting integral to translation json! */}
      <Trans parent="p" i18nKey={`${tPrefix}.invited_but_registered`}>
        {`You\'ve been invited to `}
        {{ invitationAction }} but you are already registered as a{" "}
        {{ registrationRoleName }}
        {`. `}
      </Trans>
      {/* prettier-ignore */}
      {/* Trans formatting integral to translation json! */}
    </>
  )

  const secondaryAction = invitedToDifferentRole
    ? {
        to: urls.modalRoute("/help/"),
        children: t(`${tPrefix}.contact_support`)
      }
    : null

  return (
    <PathwrightUI.Provider
      theme={{
        backgroundImage: resource.background_image_blurred_cdn,
        backgroundOverlay: resource.background_overlay
      }}
    >
      <Success
        className="Registered"
        icon={!alreadyRegistered && "check"}
        heading={
          alreadyRegistered
            ? t(`${tPrefix}.already_registered`)
            : t(`${tPrefix}.you_are_registered`)
        }
        body={roleDiff}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        meta={meta}
      >
        {!!shareInfo && (
          <Text
            el="div"
            typography="meta"
            className="Registered__share"
            inverted
          >
            {t(`${tPrefix}.share_invite`, { resourceType: resource_type })}
            <ShareIcons
              inverted
              title={shareInfo.title}
              mail={shareInfo.mail}
            />
          </Text>
        )}
        {!!trackingCode && <ExecuteScripts html={trackingCode} />}
      </Success>
    </PathwrightUI.Provider>
  )
}

Registered.displayName = "Registered"

let lastTrackedRegistrationID = null

const mapStateToProps = function (state, ownProps) {
  const { t, i18n } = ownProps
  let invitationAction, registrationRoleName, trackingCode
  const { resource, offering, already_registered } = state.registerStore.state
  const { school } = state.schoolStore.state
  const { registration, is_invite, invite_emails, invitation } =
    state.registerStore.state

  const toPathURL = state.registerStore.request.accessURL(
    resource,
    registration
  )

  // Only prompt to share if offering is public
  let shareInfo = null
  if (offering.visibility === 15) {
    shareInfo = {
      title: t(`${tPrefix}.share.title`, {
        resourceName: resource.name,
        schoolName: school.name
      }),
      mail: {
        body: t(`${tPrefix}.share.mail_body`, {
          resourceName: resource.name,
          resourceUrl: resource.url
        })
      }
    }
  }

  let resource_type = t("resource.types.course")
  if (resource.resource_type === 30)
    resource_type = t("resource.types.collection")
  if (resource.resource_type === 20) resource_type = t("resource.types.lesson")

  const shouldTrackRegistration =
    lastTrackedRegistrationID !== registration.id &&
    registration.conversion_tracking_code != null
  if (shouldTrackRegistration) {
    trackingCode = injectVariable(registration.conversion_tracking_code)
    lastTrackedRegistrationID = registration.id
  }

  if (invitation) {
    const tAction = `${tPrefix}.actions.${
      ROLE_ACTIONS[ROLE_TYPE_OFFERING][invitation.role]
    }`
    invitationAction = i18n.exists(tAction)
      ? t(tAction)
      : ROLE_ACTIONS[ROLE_TYPE_OFFERING][invitation.role]
  }
  if (registration) {
    const tRole = `${tPrefix}.roles.${
      ROLE_LABELS[ROLE_TYPE_OFFERING][registration.role]
    }`
    registrationRoleName = i18n.exists(tRole)
      ? t(tRole)
      : ROLE_LABELS[ROLE_TYPE_OFFERING][registration.role]
  }
  const alreadyRegistered = already_registered

  const props = _.assign(
    {},
    {
      // store state
      resource,
      offering,
      school,
      registration,
      is_invite,
      invite_emails,
      resource_type,
      trackingCode,
      invitation,
      invitationAction,
      registrationRoleName,
      alreadyRegistered,
      toPathURL,
      // store request
      shareInfo
    },
    ownProps
  )
  return props
}

const stores = [
  { store: "register", watch: false },
  { store: "resource", watch: false },
  { store: "school", watch: false },
  { store: "path", watch: false }
]

const RegisteredConnected = withTranslate(
  ConnectStores(Registered, stores, {}, mapStateToProps)
)

export default ({ registration, ...passProps }) => {
  const { path, loaded, refetch, cohortId, loadPath } = useContext(PathContext)

  useEffect(() => {
    if (cohortId && loaded) {
      // path has already been loaded for this cohort, so refetching will load the user's registration path
      refetch()
    } else {
      let pathId = null
      // only load the registration path if the registration is for a observer, learner, or moderator
      if (registration.role <= ENROLLMENT_ROLE_FACILITATOR) {
        pathId = registration.path
      }
      // the path has not yet been loaded for this cohort
      loadPath({
        pathId,
        cohortId: registration.offering.id,
        forceReload: true,
        // Ensures we reload from the main db, not the read replica.
        ensureConsistentRead: true
      })
    }
  }, [cohortId])

  return <RegisteredConnected {...passProps} />
}
